import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Add, Remove } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { useCartOperationStatus } from "../../../../hooks/useCartOperationStatus";
import ProductImage from "../../../../components/common/ProductImage/ProductImage";
import DeliveryMethodSelector from "../DeliveryMethodSelector/DeliveryMethodSelector";
import {
  calculateBasePrice,
  calculateOptionsSurcharge,
} from "../../../../utils/cartCalculationUtils";
import styles from "./CartItem.module.css";

interface CartItemProps {
  item: any;
  handleCartAmountChange: (quantity: number, item: any) => void;
  handleRemoveItem: (item: any) => void;
  onDeliveryDataChange?: (cartCustomId: string, data: any) => void;
  stockValidation?: any;
  allowRemoveOnOutOfStock?: boolean;
  operationInProgress?: boolean;
  optimisticQuantity?: number | null;
  // Coupon-related props
  appliedCouponCode?: string | null;
  isAffectedByCoupon?: boolean;
  couponDiscountAmount?: number;
  // Delivery method available dates callback
  onAvailableDatesChange?: (dates: Date[], method: string) => void;
}

const CartItem: React.FC<CartItemProps> = ({
  item,
  handleCartAmountChange,
  handleRemoveItem,
  onDeliveryDataChange,
  stockValidation,
  allowRemoveOnOutOfStock = false,
  operationInProgress: forcedOperation = false,
  optimisticQuantity: externalOptimisticQuantity = null,
  appliedCouponCode,
  isAffectedByCoupon = false,
  couponDiscountAmount = 0,
  onAvailableDatesChange,
}) => {
  const [localOptimisticQuantity, setLocalOptimisticQuantity] = useState<
    number | null
  >(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [localOperationType, setLocalOperationType] = useState<string | null>(
    null
  );
  const [showOptimisticPrice, setShowOptimisticPrice] = useState(false);

  const groupId =
    item.groupId ||
    (item.group && typeof item.group === "object" ? item.group.id : null);

  // Extract group object for DeliveryMethodSelector - Now properly includes deliveryMethod from backend
  const groupObject =
    item.group && typeof item.group === "object"
      ? item.group
      : { id: groupId, deliveryMethod: "standard" }; // Fallback only if group is not an object

  // DEBUG: Log group data to verify deliveryMethod is properly populated
  console.log(`🛒 [CART-ITEM] ${item.cartCustomId}:`, {
    hasGroupObject: !!(item.group && typeof item.group === "object"),
    groupObject,
    rawGroup: item.group,
    groupId,
    fallbackUsed: !(item.group && typeof item.group === "object"),
  });

  const { isLoading: itemOperationInProgress } = useCartOperationStatus(
    groupId,
    item.cartCustomId
  );

  const isOperationInProgress = itemOperationInProgress || forcedOperation;

  const hasStockIssue = stockValidation?.isValid === false;
  const availableStock = hasStockIssue
    ? stockValidation.availableStock
    : item.product?.stock || 0;

  const optimisticQuantity =
    externalOptimisticQuantity !== null
      ? externalOptimisticQuantity
      : localOptimisticQuantity;

  // Phase 2.3: Access pricing from current Redux structure with clean pricing fallback
  const basePrice =
    item.pricing?.originalUnitPrice ??
    item.cleanPricing?.pricing?.originalUnitPrice ??
    calculateBasePrice(item);
  const optionSurcharge =
    item.pricing?.customizationSurcharge ??
    item.cleanPricing?.pricing?.customizationSurcharge ??
    calculateOptionsSurcharge(item);
  const finalUnitPrice =
    item.pricing?.finalUnitPrice ??
    item.cleanPricing?.pricing?.finalUnitPrice ??
    basePrice + optionSurcharge;

  const quantity =
    optimisticQuantity !== null ? optimisticQuantity : item.quantity;
  const totalPrice =
    item.pricing?.finalTotal ??
    item.cleanPricing?.pricing?.finalTotal ??
    finalUnitPrice * quantity;

  // Phase 2.3: Extract discount information from current Redux structure with clean pricing fallback
  const promotionalDiscount =
    item.pricing?.discounts?.promotional?.totalAmount ??
    item.cleanPricing?.pricing?.discounts?.promotional?.totalAmount ??
    0;
  const couponDiscount =
    item.pricing?.discounts?.coupon?.totalAmount ??
    item.cleanPricing?.pricing?.discounts?.coupon?.totalAmount ??
    0;

  // Phase 2.3.2: Access pricing loading state from Redux
  const isPricingLoading = useSelector(
    (state: any) =>
      state.cart.pricingLoading?.items?.[item.cartCustomId] || false
  );

  const prevQuantityRef = useRef(item.quantity);

  useEffect(() => {
    if (!isOperationInProgress && (localOperationType || showOptimisticPrice)) {
      const timer = setTimeout(() => {
        setLocalOperationType(null);
        setShowOptimisticPrice(false);
        if (externalOptimisticQuantity === null) {
          setLocalOptimisticQuantity(null);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    isOperationInProgress,
    localOperationType,
    showOptimisticPrice,
    externalOptimisticQuantity,
  ]);

  useEffect(() => {
    if (item.quantity !== prevQuantityRef.current) {
      prevQuantityRef.current = item.quantity;
      setShowOptimisticPrice(false);
      if (externalOptimisticQuantity === null) {
        setLocalOptimisticQuantity(null);
      }
      setLocalOperationType(null);
    }
  }, [item.quantity, externalOptimisticQuantity]);

  useEffect(() => {
    if (externalOptimisticQuantity === null) {
      setLocalOptimisticQuantity(null);
    }
  }, [item.quantity, externalOptimisticQuantity]);

  const handleDecrease = () => {
    if (isOperationInProgress && localOperationType === "ITEM_REMOVAL") return;

    const newQuantity = quantity - 1;

    if (newQuantity <= 0) {
      setIsRemoving(true);
      setLocalOperationType("ITEM_REMOVAL");
      if (externalOptimisticQuantity === null) {
        setLocalOptimisticQuantity(0);
      }
      handleRemoveItem(item);
    } else {
      setLocalOperationType("QUANTITY_UPDATE");
      setShowOptimisticPrice(true);
      if (externalOptimisticQuantity === null) {
        setLocalOptimisticQuantity(newQuantity);
      }
      handleCartAmountChange(newQuantity, item);
    }
  };

  const handleIncrease = () => {
    if (isOperationInProgress && localOperationType === "ITEM_REMOVAL") return;
    if (quantity >= availableStock) return;

    const newQuantity = quantity + 1;
    setLocalOperationType("QUANTITY_UPDATE");
    setShowOptimisticPrice(true);
    if (externalOptimisticQuantity === null) {
      setLocalOptimisticQuantity(newQuantity);
    }
    handleCartAmountChange(newQuantity, item);
  };

  const handleRemove = () => {
    if (isOperationInProgress && localOperationType === "ITEM_REMOVAL") return;

    setIsRemoving(true);
    setLocalOperationType("ITEM_REMOVAL");
    setShowOptimisticPrice(true);
    if (externalOptimisticQuantity === null) {
      setLocalOptimisticQuantity(0);
    }
    handleRemoveItem(item);
  };

  if (quantity <= 0 && isRemoving) return null;

  const itemClasses = `${styles.cartItem} 
    ${hasStockIssue ? styles.outOfStock : ""} 
    ${isOperationInProgress ? styles.updating : ""}
    ${localOperationType === "QUANTITY_UPDATE" ? styles.quantityUpdating : ""}
    ${localOperationType === "ITEM_REMOVAL" ? styles.removing : ""}`;

  return (
    <div
      className={itemClasses}
      data-testid="cart-item"
      data-group-id={groupId}
      data-cart-custom-id={item.cartCustomId}
    >
      {isOperationInProgress && localOperationType === "ITEM_REMOVAL" && (
        <div className={styles.imageOverlayStatus}>
          <span className={styles.miniSpinner}></span>
          <span className={styles.statusTextRemoval}>Eliminando</span>
        </div>
      )}

      <Link
        to={`/p/${item.product?.url || ""}`}
        className={`${styles.imageContainer} ${
          isOperationInProgress && localOperationType === "ITEM_REMOVAL"
            ? styles.imageUpdating
            : ""
        }`}
      >
        <ProductImage
          src={item.product?.images?.[0]?.small || ""}
          alt={item.product?.name || "Product"}
          className={styles.productImage}
        />
      </Link>

      <div className={styles.mainContent}>
        {/* 1. Product Title */}
        <Link
          to={`/p/${item.product?.url || ""}`}
          className={styles.productName}
        >
          {item.product?.name || "Product"}
        </Link>

        {/* 2. Unit Price (subtle, below product name) */}
        <div className={styles.unitPriceDisplay}>
          {promotionalDiscount > 0 || couponDiscount > 0 ? (
            <>
              <span className={styles.originalUnitPrice}>
                S/ {basePrice.toFixed(2)} c/u
              </span>
              <span className={styles.discountedUnitPrice}>
                S/ {finalUnitPrice.toFixed(2)} c/u
              </span>
            </>
          ) : (
            <span className={styles.regularUnitPrice}>
              S/ {finalUnitPrice.toFixed(2)} c/u
            </span>
          )}
        </div>

        {/* 3. Product Options (groupingAttributes and customizableOptions) */}
        {(item.customizableOptions?.length > 0 ||
          item.groupingAttributes?.length > 0) && (
          <div className={styles.productOptions}>
            {item.groupingAttributes?.map((attr: any, index: number) => (
              <span key={`attr-${index}`} className={styles.optionItem}>
                {attr.name}: <strong>{attr.value}</strong>
              </span>
            ))}
            {item.customizableOptions?.map((option: any, index: number) => (
              <span key={`option-${index}`} className={styles.optionItem}>
                {option.label}:{" "}
                <strong>
                  {Array.isArray(option.value)
                    ? option.value.join(", ")
                    : option.value}
                </strong>
              </span>
            ))}
          </div>
        )}

        {/* 4. Discount Details (green, below options) */}
        {(promotionalDiscount > 0 || couponDiscount > 0) && (
          <div className={styles.discountDetails}>
            {promotionalDiscount > 0 && (
              <div className={styles.discountDetailRow}>
                Descuentos Promocionales: -S/ {promotionalDiscount.toFixed(2)}
              </div>
            )}
            {couponDiscount > 0 && (
              <div className={styles.discountDetailRow}>
                Cupones{appliedCouponCode ? ` (${appliedCouponCode})` : ""}: -S/{" "}
                {couponDiscount.toFixed(2)}
              </div>
            )}
          </div>
        )}

        {/* 5. Delivery Method Selector (moved to bottom) */}
        <div className={styles.deliveryMethodInline}>
          <DeliveryMethodSelector
            item={item}
            group={groupObject}
            disabled={isOperationInProgress || hasStockIssue}
            onAvailableDatesChange={onAvailableDatesChange}
          />
        </div>
      </div>

      {/* Right Column: Price, Quantity, and Remove Button (stacked vertically) */}
      <div className={styles.rightControls}>
        {/* 1. Total Price Section */}
        <div className={styles.totalPriceSection}>
          {isPricingLoading ? (
            <div className={styles.pricingLoader}>
              <CircularProgress size={16} />
              <span className={styles.loadingText}>Calculando...</span>
            </div>
          ) : (
            <>
              {promotionalDiscount > 0 || couponDiscount > 0 ? (
                <>
                  <span className={styles.originalTotalPrice}>
                    S/ {(basePrice * quantity).toFixed(2)}
                  </span>
                  <span className={styles.discountedTotalPrice}>
                    S/ {totalPrice.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className={styles.regularTotalPrice}>
                  S/ {totalPrice.toFixed(2)}
                </span>
              )}
            </>
          )}
        </div>

        {/* 2. Quantity Controls */}
        <div className={styles.qtyControls}>
          <button
            className={styles.qtyButton}
            onClick={handleDecrease}
            disabled={localOperationType === "ITEM_REMOVAL"}
            aria-label="Decrease quantity"
          >
            <Remove fontSize="small" />
          </button>

          <span
            className={`${styles.qtyText} ${
              optimisticQuantity !== null &&
              optimisticQuantity !== item.quantity &&
              showOptimisticPrice
                ? styles.optimisticQty
                : ""
            }`}
          >
            {quantity}
          </span>

          <button
            className={styles.qtyButton}
            onClick={handleIncrease}
            disabled={
              localOperationType === "ITEM_REMOVAL" ||
              quantity >= availableStock
            }
            aria-label="Increase quantity"
          >
            <Add fontSize="small" />
          </button>
        </div>

        {/* 3. Remove Button */}
        <button
          className={`${styles.removeButton} ${
            hasStockIssue ? styles.outOfStockRemoveButton : ""
          }`}
          onClick={handleRemove}
          disabled={
            isOperationInProgress && localOperationType === "ITEM_REMOVAL"
          }
        >
          Eliminar
        </button>
      </div>

      {hasStockIssue && (
        <div className={styles.stockWarning}>
          <span className={styles.stockWarningText}>
            {stockValidation.availableStock === 0
              ? "Sin stock"
              : `Solo ${stockValidation.availableStock} disponible${
                  stockValidation.availableStock > 1 ? "s" : ""
                }`}
          </span>
        </div>
      )}
    </div>
  );
};

export default CartItem;
